<template>
  <clinician-app-layout>
    <div class="mx-auto max-w-6xl sm:px-6 lg:px-8 py-6">
      <div class="pb-5 sm:flex sm:items-center sm:justify-between">
        <h2 class="text-3xl text-vice-teal-900 font-semibold leading-tight">
          Device & session setup
        </h2>
        <RunTestSession v-if="!stepMode" />
      </div>
      <div>
        <span class="text-md text-gray-500">
          Walk through each step to configure your devices before starting a session.
        </span>
        <div class="select-usecase relative">
          <div class="flex justify-end items-center">
            <jet-label for="use-case" value="Use-case:" class="pl-4 pr-2 bg-light-grey z-10" />
            <select
              v-model="currentUseCaseId"
              id="use-case"
              class="
                border-gray-300
                focus:ring-vice-teal-500
                focus:border-vice-teal-500
                rounded-md
                shadow-sm
                sm:text-sm
                text-gray-900
                z-10
              "
            >
              <option v-for="useCase in useCasesNames" :value="useCase.id" :key="useCase.id">
                {{ useCase.name }}
              </option>
            </select>
          </div>
        </div>
        <div v-if="stepMode" class="mb-10">
          <nav aria-label="Progress">
            <ol role="list" class="flex items-center">
              <li
                v-for="(currentUseCasePrio, index) in currentUseCase.device_type_usage_priorities"
                :key="currentUseCasePrio.id"
                :class="{
                  'pr-8 relative sm:pr-16':
                    index !== currentUseCase.device_type_usage_priorities.length - 1,
                }"
              >
                <div class="flex items-center">
                  <div
                    class="h-9 rounded-full flex items-center justify-center text-sm font-bold"
                    :class="[
                      {
                        'bg-vice-teal-600 hover:bg-vice-teal-900 text-white': index === currentStep,
                        'bg-gray-150 hover:bg-gray-300 text-gray-500': index > currentStep,
                        'bg-vice-teal-50 text-vice-teal-600': index < currentStep,
                      },
                    ]"
                  >
                    <span class="px-4">
                      {{ index + 1 }}
                    </span>
                    <span class="text-sm font-semibold mr-3">
                      {{ currentUseCasePrio.name }}
                    </span>
                  </div>
                </div>
                <div
                  v-if="index !== currentUseCase.device_type_usage_priorities.length - 1"
                  class="absolute top-4 right-2 mt-0.5 h-0.5 w-12 bg-gray-200"
                  aria-hidden="true"
                />
              </li>
            </ol>
          </nav>
        </div>
        <ul v-if="currentUseCase && currentUseCase.device_type_usage_priorities">
          <li
            v-for="(
              currentUseCasePrio, currentUseCasePrioIndex
            ) in currentUseCase.device_type_usage_priorities"
            :key="currentUseCasePrio.id"
            v-show="!stepMode || currentUseCasePrioIndex === currentStep"
            class="mb-8 relative"
          >
            <div class="use-case-priority">
              <div class="use-case-priority-header">
                <div class="use-case-name-priority">
                  <div class="relative flex items-center h-10">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300" />
                    </div>
                    <div class="relative flex justify-start pb-px">
                      <span class="pr-3 pb-px bg-light-grey text-lg font-medium text-vice-teal-700">
                        {{ currentUseCasePrio.name }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="use-case-priority-description">
                  <div class="relative flex w-2/3">
                    <span class="pr-3 bg-light-grey text-xs font-light">
                      {{ currentUseCasePrio.description }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="use-case-slots mt-6 mb-6">
                <ul v-if="currentUseCasePrio && currentUseCasePrio.device_type_use_case_slots">
                  <!-- Essential devices -->
                  <div v-if="currentUseCasePrioIndex === 0" class="grid grid-cols-3 w-full gap-6">
                    <!-- 1st card -->
                    <template v-if="stepMode">
                      <li
                        v-for="slot in currentUseCasePrio.device_type_use_case_slots"
                        :key="slot.id"
                      >
                        <UseCaseSlot
                          :useCaseId="currentUseCase.id"
                          :deviceSlotId="slot.id"
                          :deviceTypeLabel="slot.device_type.label"
                          :deviceKind="slot.device_kind"
                          :deviceTypeIcon="slot.device_type.icon"
                          :uiNotificationType="slot.ui_notification_type"
                          :uiNotificationTitle="slot.ui_notification_title"
                          :uiNotification="slot.ui_notification"
                          :storedClinicianDevice="slot.stored_clinician_device?.device_data"
                        />
                      </li>
                    </template>
                    <li v-else>
                      <TestConnectionSlot />
                    </li>
                    <!-- 2nd card -->
                    <li>
                      <DefaultMicrophoneSlot v-if="stepMode" />
                      <template v-else>
                        <li
                          v-for="slot in currentUseCasePrio.device_type_use_case_slots"
                          :key="slot.id"
                        >
                          <UseCaseSlot
                            :useCaseId="currentUseCase.id"
                            :deviceSlotId="slot.id"
                            :deviceTypeLabel="slot.device_type.label"
                            :deviceKind="slot.device_kind"
                            :deviceTypeIcon="slot.device_type.icon"
                            :uiNotificationType="slot.ui_notification_type"
                            :uiNotificationTitle="slot.ui_notification_title"
                            :uiNotification="slot.ui_notification"
                            :storedClinicianDevice="slot.stored_clinician_device?.device_data"
                          />
                        </li>
                      </template>
                    </li>
                    <!-- 3rd card -->
                    <li>
                      <DefaultSpeakerSlot v-if="stepMode" />
                      <DefaultDevicesSlot v-else />
                    </li>
                  </div>

                  <!-- Medical devices -->
                  <div v-else class="grid grid-cols-3 w-full gap-6">
                    <li
                      v-for="slot in currentUseCasePrio.device_type_use_case_slots"
                      :key="slot.id"
                    >
                      <UseCaseSlot
                        :useCaseId="currentUseCase.id"
                        :deviceSlotId="slot.id"
                        :deviceTypeLabel="slot.device_type.label"
                        :deviceKind="slot.device_kind"
                        :deviceTypeIcon="slot.device_type.icon"
                        :uiNotificationType="slot.ui_notification_type"
                        :uiNotificationTitle="slot.ui_notification_title"
                        :uiNotification="slot.ui_notification"
                        :storedClinicianDevice="slot.stored_clinician_device?.device_data"
                      />
                    </li>
                  </div>
                </ul>
              </div>
            </div>
          </li>
        </ul>
        <div v-if="stepMode" class="flex justify-between items-center mt-10">
          <div>
            <jet-secondary-button v-if="currentStep > 0" class="h-10" @click="prevStep">
              Previous
            </jet-secondary-button>
          </div>
          <div class="flex space-x-4">
            <jet-secondary-button
              class="h-10"
              v-if="currentStep < currentUseCase.device_type_usage_priorities.length - 1"
              @click="nextStep"
            >
              Skip for now
            </jet-secondary-button>
            <jet-button
              v-if="currentStep < currentUseCase.device_type_usage_priorities.length - 1"
              @click="nextStep"
            >
              Next step
            </jet-button>
            <inertia-link :href="route('dashboard.clinician')" class="inline-block">
              <jet-button
                v-if="currentStep === currentUseCase.device_type_usage_priorities.length - 1"
              >
                Finish setup
              </jet-button>
            </inertia-link>
          </div>
        </div>
      </div>
      <SetDeviceModal></SetDeviceModal>
      <DeleteDeviceModal></DeleteDeviceModal>
    </div>
  </clinician-app-layout>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3';
import { ref, computed, watch } from 'vue';
import useDeviceAndSessionSetupStore from '@/Stores/useDeviceAndSessionSetupStore';
import SetDeviceModal from '@/Components/Modals/DeviceAndSessionSetup/SetDeviceModal.vue';
import DeleteDeviceModal from '@/Components/Modals/DeviceAndSessionSetup/DeleteDeviceModal.vue';
import ClinicianAppLayout from '@/Layouts/ClinicianAppLayout.vue';
import UseCaseSlot from '@/Components/DeviceAndSessionSetup/UseCaseSlot.vue';
import JetLabel from '@/Jetstream/Label.vue';
import DefaultDevicesSlot from '@/Components/DeviceAndSessionSetup/DefaultDevicesSlot.vue';
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';
import JetButton from '@/Jetstream/Button.vue';
import DefaultMicrophoneSlot from '@/Components/DeviceAndSessionSetup/DefaultMicrophoneSlot.vue';
import TestConnectionSlot from '@/Components/DeviceAndSessionSetup/TestConnectionSlot.vue';
import DefaultSpeakerSlot from '@/Components/DeviceAndSessionSetup/DefaultSpeakerSlot.vue';
import RunTestSession from '@/Components/Cards/Session/RunTestSession.vue';

export default {
  name: 'DeviceAndSessionSetup',
  components: {
    RunTestSession,
    DefaultSpeakerSlot,
    TestConnectionSlot,
    DefaultMicrophoneSlot,
    ClinicianAppLayout,
    UseCaseSlot,
    SetDeviceModal,
    DeleteDeviceModal,
    JetLabel,
    JetButton,
    DefaultDevicesSlot,
    JetSecondaryButton,
  },
  setup() {
    const { props } = usePage();

    const deviceAndSessionSetupStore = useDeviceAndSessionSetupStore();

    const deviceTypesUseCases = ref([]);
    const currentUseCase = ref({});
    const currentUseCaseId = ref(null);
    const stepMode = ref(props.value.stepMode);
    const currentStep = ref(0);

    // storing use cases to Pinia storage
    deviceAndSessionSetupStore.setDeviceTypesUseCases(props.value.deviceTypesUseCases);

    // storing clinician default devices (microphone and speaker) to Pinia store
    deviceAndSessionSetupStore.setDefaultClinicianDevices(props.value.defaultDevices);

    // getting use cases from Pinia storage
    deviceTypesUseCases.value = deviceAndSessionSetupStore.getDeviceTypesUseCases;

    currentUseCaseId.value = deviceTypesUseCases.value.length
      ? deviceTypesUseCases.value[0].id
      : null;

    currentUseCase.value = deviceTypesUseCases.value.find(
      (useCase) => useCase.id === currentUseCaseId.value,
    );

    const useCasesNames = computed(() => {
      const names = [];
      Object.values(deviceTypesUseCases.value).forEach((useCase) => {
        names.push({
          id: useCase.id,
          name: useCase.name,
        });
      });
      return names;
    });

    const nextStep = () => {
      if (currentStep.value < currentUseCase.value.device_type_usage_priorities.length - 1) {
        currentStep.value += 1;
      }
    };

    const prevStep = () => {
      if (currentStep.value > 0) {
        currentStep.value -= 1;
      }
    };

    watch(currentUseCaseId, (newValue) => {
      currentUseCase.value = deviceTypesUseCases.value.find((useCase) => useCase.id === newValue);
      currentStep.value = 0;
    });

    return {
      useCasesNames,
      currentUseCaseId,
      currentUseCase,
      stepMode,
      currentStep,
      nextStep,
      prevStep,
    };
  },
};
</script>

<style scoped>
.select-usecase {
  top: 40px;
}
</style>
